import { useState } from 'react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from './alert-dialog';
import { Input } from './input';
import { Label } from './label';

/**
 * ConfirmationDialog - A reusable confirmation dialog component
 * 
 * @param {boolean} open - Controls visibility of the dialog
 * @param {function} onOpenChange - Callback when dialog open state changes
 * @param {string} title - Dialog title
 * @param {string} description - Dialog description/message
 * @param {function} onConfirm - Callback when confirmed (receives promptValue if hasPrompt is true)
 * @param {function} onCancel - Optional callback when cancelled
 * @param {string} confirmText - Text for confirm button (default: "Confirm")
 * @param {string} cancelText - Text for cancel button (default: "Cancel")
 * @param {string} variant - Visual variant: "default", "destructive" (default: "default")
 * @param {boolean} hasPrompt - Whether to show an input prompt
 * @param {string} promptLabel - Label for the prompt input
 * @param {string} promptPlaceholder - Placeholder for the prompt input
 */
export const ConfirmationDialog = ({
    open,
    onOpenChange,
    title,
    description,
    onConfirm,
    onCancel,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    variant = 'default',
    hasPrompt = false,
    promptLabel = '',
    promptPlaceholder = '',
}) => {
    const [promptValue, setPromptValue] = useState('');

    const handleConfirm = () => {
        if (hasPrompt) {
            onConfirm(promptValue);
        } else {
            onConfirm();
        }
        setPromptValue('');
    };

    const handleCancel = () => {
        onCancel?.();
        onOpenChange(false);
        setPromptValue('');
    };

    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>{title}</AlertDialogTitle>
                    <AlertDialogDescription>{description}</AlertDialogDescription>
                </AlertDialogHeader>

                {hasPrompt && (
                    <div className="py-4 space-y-2">
                        {promptLabel && <Label>{promptLabel}</Label>}
                        <Input
                            value={promptValue}
                            onChange={(e) => setPromptValue(e.target.value)}
                            placeholder={promptPlaceholder}
                        />
                    </div>
                )}

                <AlertDialogFooter>
                    <AlertDialogCancel onClick={handleCancel}>
                        {cancelText}
                    </AlertDialogCancel>
                    <AlertDialogAction
                        onClick={handleConfirm}
                        className={variant === 'destructive' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
                    >
                        {confirmText}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};

/**
 * Hook to manage confirmation dialog state
 * Returns state and helpers to show confirmation dialogs
 */
export const useConfirmation = () => {
    const [state, setState] = useState({
        open: false,
        title: '',
        description: '',
        confirmText: 'Confirm',
        cancelText: 'Cancel',
        variant: 'default',
        hasPrompt: false,
        promptLabel: '',
        promptPlaceholder: '',
        onConfirm: () => { },
        onCancel: () => { },
    });

    const confirm = (options) => {
        return new Promise((resolve) => {
            setState({
                open: true,
                title: options.title || 'Confirm',
                description: options.description || 'Are you sure?',
                confirmText: options.confirmText || 'Confirm',
                cancelText: options.cancelText || 'Cancel',
                variant: options.variant || 'default',
                hasPrompt: options.hasPrompt || false,
                promptLabel: options.promptLabel || '',
                promptPlaceholder: options.promptPlaceholder || '',
                onConfirm: (promptValue) => {
                    setState(prev => ({ ...prev, open: false }));
                    resolve(options.hasPrompt ? { confirmed: true, value: promptValue } : true);
                },
                onCancel: () => {
                    setState(prev => ({ ...prev, open: false }));
                    resolve(options.hasPrompt ? { confirmed: false, value: null } : false);
                },
            });
        });
    };

    const setOpen = (open) => {
        setState(prev => ({ ...prev, open }));
        if (!open) {
            state.onCancel?.();
        }
    };

    return {
        dialogProps: {
            open: state.open,
            onOpenChange: setOpen,
            title: state.title,
            description: state.description,
            confirmText: state.confirmText,
            cancelText: state.cancelText,
            variant: state.variant,
            hasPrompt: state.hasPrompt,
            promptLabel: state.promptLabel,
            promptPlaceholder: state.promptPlaceholder,
            onConfirm: state.onConfirm,
            onCancel: state.onCancel,
        },
        confirm,
    };
};

export default ConfirmationDialog;
